import { useCallback, useEffect, useRef, useState } from "react";
import { View } from "react-native";

import { useAuth } from "@clerk/clerk-expo";
import { useFocusEffect } from "expo-router";

import { parsePlayedOn } from "./played-on";
import { createPlayer, fetchPlayers, logPlay, setParticipants, type Player } from "./api";
import {
  ActionRow,
  Button,
  Chip,
  ChipRow,
  ErrorText,
  Field,
  Label,
  Meta,
  RatingPicker,
} from "./ui";

/**
 * Log a play (WI-036, ADR-0007, WI-045).
 *
 * The whole design is in the first button: **Played** records a complete
 * session with one tap and no form. Everything else is behind "Add detail",
 * closed by default, and nothing in it is required. Schema depth is not form
 * depth -- the API accepts an empty body, so the fast path sends one.
 */

type Stage = "idle" | "saving" | "saved" | "detail";

export function LogPlay({ slug, onLogged }: { slug: string; onLogged?: () => void }) {
  const { isLoaded, isSignedIn } = useAuth();

  const [stage, setStage] = useState<Stage>("idle");
  const [error, setError] = useState<string | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  /**
   * Blank means today, the overwhelmingly common case, and it must stay a
   * non-decision. The API defaults `playedAt` to now when it is absent, so an
   * empty field sends nothing rather than today's date -- the same result, and
   * it keeps the one-tap path honest.
   */
  const [playedOn, setPlayedOn] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [winner, setWinner] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (!isSignedIn || stage !== "detail") return;
    const controller = new AbortController();
    fetchPlayers(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setPlayers(found);
      })
      .catch(() => {
        // A roster that will not load should not stop somebody logging a play.
      });
    return () => controller.abort();
  }, [isSignedIn, stage]);

  const reset = useCallback(() => {
    setStage("idle");
    setRating(null);
    setNotes("");
    setPlayedOn("");
    setChosen(new Set());
    setWinner(null);
    setError(null);
  }, []);

  /**
   * The confirmation is transient.
   *
   * It used to be terminal, which meant the Played button vanished for good:
   * Expo Router keeps a screen mounted in the stack, so going back and
   * returning showed "Logged" from the previous visit with no way to record
   * another play. Logging the same game twice is supported by design
   * (ADR-0006 keeps every play), so the UI must not make it a dead end.
   */
  useEffect(() => {
    if (stage !== "saved") return;
    resetTimer.current = setTimeout(reset, 4000);
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, [stage, reset]);

  /** Returning to a mounted screen should not show a stale confirmation. */
  useFocusEffect(
    useCallback(() => {
      return () => {
        if (resetTimer.current) clearTimeout(resetTimer.current);
        setStage((current) => (current === "saved" ? "idle" : current));
      };
    }, []),
  );

  /** The one-tap path. No participants, no rating, no form. */
  const logNow = useCallback(() => {
    if (stage === "saving") return;
    setStage("saving");
    setError(null);

    logPlay(slug)
      .then(() => {
        setStage("saved");
        onLogged?.();
      })
      .catch((cause: Error) => {
        setStage("idle");
        setError(cause.message);
      });
  }, [slug, stage, onLogged]);

  /** The expanded path. Still nothing required. */
  const logWithDetail = useCallback(() => {
    if (stage === "saving") return;

    const played = parsePlayedOn(playedOn);
    if (played === "invalid") {
      // Checked before the spinner starts: refusing and failing must not look
      // the same.
      setError("That date did not make sense. Use YYYY-MM-DD, or leave it blank for today.");
      return;
    }

    setStage("saving");
    setError(null);

    logPlay(slug, {
      rating,
      notes: notes.trim() === "" ? null : notes.trim(),
      ...(played === null ? {} : { playedAt: played.toISOString() }),
    })
      .then(async (session) => {
        if (chosen.size > 0) {
          // A second call, because participants need the session id. A failure
          // must not lose the play already recorded.
          await setParticipants(
            session.id,
            [...chosen].map((playerId) => ({
              playerId,
              isWinner: winner === null ? null : playerId === winner,
            })),
          ).catch(() => {
            setError("The play was saved, but the players could not be attached.");
          });
        }
        setStage("saved");
        onLogged?.();
      })
      .catch((cause: Error) => {
        setStage("detail");
        setError(cause.message);
      });
  }, [slug, stage, rating, notes, playedOn, chosen, winner, onLogged]);

  const addPlayer = useCallback(() => {
    const name = newName.trim();
    if (name === "") return;
    setNewName("");

    createPlayer(name)
      .then((player) => {
        setPlayers((current) => [player, ...current]);
        setChosen((current) => new Set(current).add(player.id));
      })
      .catch((cause: Error) => setError(cause.message));
  }, [newName]);

  const toggle = useCallback((id: string) => {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setWinner((current) => (current === id ? null : current));
  }, []);

  if (!isLoaded || !isSignedIn) return null;

  if (stage === "saved") {
    return (
      <ActionRow>
        <Meta>Logged. Your shelf is updated.</Meta>
        <Button label="Log another" onPress={reset} />
      </ActionRow>
    );
  }

  const detail = stage === "detail";
  const playing = players.filter((p) => chosen.has(p.id));

  return (
    <View style={{ gap: 8 }}>
      <ActionRow>
        <Button
          label={detail ? "Save play" : "Played"}
          variant="primary"
          onPress={detail ? logWithDetail : logNow}
          busy={stage === "saving"}
        />
        {!detail && (
          <Button label="Add detail" onPress={() => setStage("detail")} disabled={stage === "saving"} />
        )}
      </ActionRow>

      <ErrorText>{error}</ErrorText>

      {detail && (
        <View style={{ gap: 8 }}>
          <Label>Rating</Label>
          <RatingPicker value={rating} onChange={setRating} />

          <Label>Played on</Label>
          <Field
            value={playedOn}
            onChangeText={setPlayedOn}
            placeholder="today"
            autoCapitalize="none"
            autoCorrect={false}
            inputMode="numeric"
          />
          <Meta>YYYY-MM-DD. Leave blank for today.</Meta>

          <Label>Who played</Label>
          {players.length === 0 && <Meta>Nobody on your roster yet.</Meta>}
          <ChipRow>
            {players.map((p) => (
              <Chip
                key={p.id}
                label={p.displayName}
                selected={chosen.has(p.id)}
                onPress={() => toggle(p.id)}
              />
            ))}
          </ChipRow>

          {/*
            The same visible row as the session editor (WI-043). This used to
            be a long-press with a line of text explaining it, which nobody
            finds on the surface this project actually deploys -- and having
            two different gestures for one idea, one screen apart, is exactly
            the inconsistency WI-045 exists to remove.
          */}
          {chosen.size > 0 && (
            <>
              <Label>Who won</Label>
              <ChipRow>
                {playing.map((p) => (
                  <Chip
                    key={p.id}
                    label={winner === p.id ? `🏆 ${p.displayName}` : p.displayName}
                    selected={winner === p.id}
                    tone="win"
                    onPress={() => setWinner(winner === p.id ? null : p.id)}
                  />
                ))}
                <Chip label="Nobody won" selected={winner === null} onPress={() => setWinner(null)} />
              </ChipRow>
            </>
          )}

          <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
            <Field
              value={newName}
              onChangeText={setNewName}
              onSubmitEditing={addPlayer}
              placeholder="Add someone"
              returnKeyType="done"
              style={{ flex: 1 }}
            />
            <Button label="Add" onPress={addPlayer} />
          </View>

          <Label>Notes</Label>
          <Field
            value={notes}
            onChangeText={setNotes}
            placeholder="Anything worth remembering"
            multiline
          />
        </View>
      )}
    </View>
  );
}
